import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { getApiUrl } from '../../config/api';
import { ArrowLeft, Download, FileText, Calendar, Utensils } from 'lucide-react';

// Utility function to format date without timezone conversion
const formatDate = (dateString) => {
  if (!dateString) return 'No date';
  // Split the date string and format it directly to avoid timezone issues
  const [year, month, day] = dateString.split('T')[0].split('-');
  return `${month}/${day}/${year}`;
};

export default function HorsesFeedReport() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [horses, setHorses] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [plansRes, horsesRes] = await Promise.all([
        axios.get(getApiUrl('/api/horses/feed/plans')),
        axios.get(getApiUrl('/api/horses'))
      ]);
      
      const plansData = Array.isArray(plansRes.data) ? plansRes.data : [];
      const horsesData = Array.isArray(horsesRes.data) ? horsesRes.data : [];
      
      // Fetch detailed plan items for each plan
      const plansWithItems = await Promise.all(
        plansData.map(async (plan) => {
          try {
            const itemsRes = await axios.get(getApiUrl(`/api/horses/feed/plans/${plan.id}`));
            return {
              ...plan,
              items: Array.isArray(itemsRes.data.items) ? itemsRes.data.items : []
            };
          } catch (error) {
            console.error(`Error fetching items for plan ${plan.id}:`, error);
            return { ...plan, items: [] };
          }
        })
      );
      
      setPlans(plansWithItems);
      setHorses(horsesData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setPlans([]);
      setHorses([]);
    } finally {
      setLoading(false);
    }
  };

  const exportToCSV = () => {
    const csvData = [];
    
    // Header row
    csvData.push([
      'Horse Name',
      'Plan Start Date',
      'Plan End Date',
      'Plan Notes',
      'Product Name',
      'Amount',
      'Unit',
      'Frequency',
      'Instructions'
    ]);

    // Group plans by horse
    const plansByHorse = plans.reduce((acc, plan) => {
      if (!acc[plan.horse_id]) {
        acc[plan.horse_id] = [];
      }
      acc[plan.horse_id].push(plan);
      return acc;
    }, {});

    // Add data rows
    Object.values(plansByHorse).forEach(horsePlans => {
      horsePlans.forEach(plan => {
        if (plan.items && plan.items.length > 0) {
          plan.items.forEach(item => {
            csvData.push([
              plan.horse_name || 'Unknown Horse',
              plan.start_date || '',
              plan.end_date || '',
              plan.notes || '',
              item.product_name || '',
              item.amount || '',
              item.unit || '',
              item.frequency || '',
              item.instructions || ''
            ]);
          });
        } else {
          // Plan with no items
          csvData.push([
            plan.horse_name || 'Unknown Horse',
            plan.start_date || '',
            plan.end_date || '',
            plan.notes || '',
            '', '', '', '', ''
          ]);
        }
      });
    });

    // Convert to CSV string
    const csvString = csvData.map(row => 
      row.map(field => `"${String(field).replace(/"/g, '""')}"`).join(',')
    ).join('\n');

    // Download file
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `feed-plans-report-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    // Simple HTML to PDF conversion
    const printWindow = window.open('', '_blank');
    const htmlContent = generatePDFContent();
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };

  const generatePDFContent = () => {
    const plansByHorse = plans.reduce((acc, plan) => {
      if (!acc[plan.horse_id]) {
        acc[plan.horse_id] = [];
      }
      acc[plan.horse_id].push(plan);
      return acc;
    }, {});

    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Feed Plans Report</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #333; border-bottom: 2px solid #333; padding-bottom: 10px; }
          h2 { color: #666; margin-top: 30px; }
          .plan { margin: 20px 0; padding: 15px; border: 1px solid #ddd; border-radius: 5px; }
          .plan-header { font-weight: bold; margin-bottom: 10px; }
          .plan-dates { color: #666; font-size: 0.9em; margin-bottom: 10px; }
          .plan-notes { margin-bottom: 15px; font-style: italic; }
          .items-table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          .items-table th, .items-table td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          .items-table th { background-color: #f5f5f5; font-weight: bold; }
        </style>
      </head>
      <body>
        <h1>Feed Plans Report</h1>
        <p>Generated on: ${formatDate(new Date().toISOString().split('T')[0])}</p>
    `;

    Object.values(plansByHorse).forEach(horsePlans => {
      const horseName = horsePlans[0]?.horse_name || 'Unknown Horse';
      html += `<h2>${horseName}</h2>`;
      
      horsePlans.forEach(plan => {
        html += `
          <div class="plan">
            <div class="plan-header">
              Feed Plan
            </div>
            <div class="plan-dates">
              ${plan.start_date ? `Start: ${formatDate(plan.start_date)}` : 'No start date'}
              ${plan.end_date ? ` | End: ${formatDate(plan.end_date)}` : ''}
            </div>
            ${plan.notes ? `<div class="plan-notes">Notes: ${plan.notes}</div>` : ''}
        `;
        
        if (plan.items && plan.items.length > 0) {
          html += `
            <table class="items-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Amount</th>
                  <th>Unit</th>
                  <th>Frequency</th>
                  <th>Instructions</th>
                </tr>
              </thead>
              <tbody>
          `;
          
          plan.items.forEach(item => {
            html += `
              <tr>
                <td>${item.product_name || ''}</td>
                <td>${item.amount || ''}</td>
                <td>${item.unit || ''}</td>
                <td>${item.frequency || ''}</td>
                <td>${item.instructions || ''}</td>
              </tr>
            `;
          });
          
          html += '</tbody></table>';
        } else {
          html += '<p><em>No feed items in this plan</em></p>';
        }
        
        html += '</div>';
      });
    });

    html += '</body></html>';
    return html;
  };

  // Group plans by horse for display
  const plansByHorse = plans.reduce((acc, plan) => {
    if (!acc[plan.horse_id]) {
      acc[plan.horse_id] = [];
    }
    acc[plan.horse_id].push(plan);
    return acc;
  }, {});

  return (
    <div className="p-4 sm:p-6 min-w-0">
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h2 className="text-xl sm:text-2xl font-bold flex items-center">
            <FileText className="w-6 h-6 mr-2" />
            Feed Plans Report
          </h2>
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <button
              onClick={exportToCSV}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded hover:bg-green-700 text-sm"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Export CSV</span>
            </button>
            <button
              onClick={exportToPDF}
              className="flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded hover:bg-red-700 text-sm"
            >
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Export PDF</span>
            </button>
            <button
              onClick={() => navigate('/horses/feedplan')}
              className="flex items-center gap-2 px-3 py-2 border rounded hover:bg-gray-50 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Return to Feed Plans</span>
            </button>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p className="mt-2 text-gray-600">Loading feed plans...</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Utensils className="w-12 h-12 mx-auto mb-4 text-gray-400" />
          <p>No feed plans found. Create some feed plans to generate a report.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {Object.entries(plansByHorse).map(([horseId, horsePlans]) => (
            <div key={horseId} className="bg-white rounded-lg shadow">
              <div className="p-4 border-b">
                <h3 className="text-xl font-semibold flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  {horsePlans[0]?.horse_name || 'Unknown Horse'}
                </h3>
              </div>
              
              <div className="p-4 space-y-4">
                {horsePlans.map((plan) => (
                  <div key={plan.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                      <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                        <span className="text-sm text-gray-600 break-words">
                          {plan.start_date ? formatDate(plan.start_date) : 'No start date'}
                          {plan.end_date && ` - ${formatDate(plan.end_date)}`}
                        </span>
                      </div>
                    </div>
                    
                    {plan.notes && (
                      <p className="text-sm text-gray-700 mb-3 italic break-words">"{plan.notes}"</p>
                    )}
                    
                    {plan.items && plan.items.length > 0 ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm min-w-[600px]">
                          <thead>
                            <tr className="border-b">
                              <th className="text-left py-2 font-medium px-2">Product</th>
                              <th className="text-left py-2 font-medium px-2">Amount</th>
                              <th className="text-left py-2 font-medium px-2">Unit</th>
                              <th className="text-left py-2 font-medium px-2">Frequency</th>
                              <th className="text-left py-2 font-medium px-2">Instructions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {plan.items.map((item, idx) => (
                              <tr key={idx} className="border-b">
                                <td className="py-2 px-2 break-words">{item.product_name || ''}</td>
                                <td className="py-2 px-2 whitespace-nowrap">{item.amount || ''}</td>
                                <td className="py-2 px-2 whitespace-nowrap">{item.unit || ''}</td>
                                <td className="py-2 px-2 whitespace-nowrap">{item.frequency || ''}</td>
                                <td className="py-2 px-2 break-words">{item.instructions || ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 italic">No feed items in this plan</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
